interface ClassBreak {
  min: number;
  max: number;
}

export const calculateClassBreaks = (
  values: number[],
  mode: 'jenks' | 'equalInterval' | 'quantile' | 'headsTails' | 'manual',
  numberOfClasses: number,
): ClassBreak[] => {
  if (!values.length) return [];

  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) return [];

  const sortedValues = [...finite].sort((a, b) => a - b);
  const min = sortedValues[0];
  const max = sortedValues[sortedValues.length - 1];
  const classes = clampClassCount(numberOfClasses, sortedValues.length);

  // Degenerate: all values identical (or only one sample) → single class.
  if (min === max || classes <= 1) {
    return [{ min, max }];
  }

  switch (mode) {
    case 'equalInterval':
      return calculateEqualInterval(min, max, classes);

    case 'quantile':
      return calculateQuantile(sortedValues, classes);

    case 'jenks':
      return calculateJenks(sortedValues, classes);

    case 'headsTails':
      return calculateHeadsTails(sortedValues, classes);

    case 'manual':
      // TODO: handle manual case
      return calculateEqualInterval(min, max, classes);

    default:
      return calculateEqualInterval(min, max, classes);
  }
};

/** Keep class count in a usable range given the sample size. */
const clampClassCount = (requested: number, n: number): number => {
  if (!Number.isFinite(requested) || requested < 1) return 1;
  return Math.min(Math.floor(requested), n);
};

const calculateEqualInterval = (min: number, max: number, classes: number): ClassBreak[] => {
  const interval = (max - min) / classes;
  const breaks: ClassBreak[] = [];

  for (let i = 0; i < classes; i++) {
    breaks.push({
      min: min + i * interval,
      max: min + (i + 1) * interval,
    });
  }

  return breaks;
};

/**
 * Quantile: each class contains (as close as possible) the same number of features.
 * Break boundaries are taken from the sorted sample at equal-count cut points so
 * adjacent classes share edges (SLD uses [min, max) except the last class).
 */
const calculateQuantile = (sortedValues: number[], classes: number): ClassBreak[] => {
  const n = sortedValues.length;
  const breaks: ClassBreak[] = [];

  for (let i = 0; i < classes; i++) {
    // Inclusive start index / exclusive end index into the sorted array.
    const start = Math.floor((i * n) / classes);
    const end = Math.floor(((i + 1) * n) / classes); // exclusive
    const lastIdx = Math.max(start, end - 1);

    const min = sortedValues[start];
    // Upper edge: next class's first value when possible so ranges abut cleanly;
    // last class closes on the global max.
    const max = i === classes - 1 ? sortedValues[n - 1] : sortedValues[Math.min(end, n - 1)];

    breaks.push({ min, max: max < min ? sortedValues[lastIdx] : max });
  }

  // If heavy ties make two adjacent classes share the same min==max, collapse
  // empty interior ranges while keeping class count when values still differ.
  return normalizeAbuttingBreaks(breaks, sortedValues[0], sortedValues[n - 1]);
};

/**
 * Jenks (natural breaks): minimize within-class variance / maximize between-class
 * variance via the classic Fisher–Jenks dynamic-programming algorithm.
 * Distinct from quantile — class sizes may be unequal when the data clusters.
 */
const calculateJenks = (sortedValues: number[], classes: number): ClassBreak[] => {
  const n = sortedValues.length;
  const matrices = jenksMatrices(sortedValues, classes);
  const kclass = jenksBreaks(sortedValues, matrices.lowerClassLimits, classes);

  // kclass holds the lower bound of each class plus the global max at the end:
  // [min, b1, b2, ..., max] → classes are [kclass[i], kclass[i+1]].
  const breaks: ClassBreak[] = [];
  for (let i = 0; i < classes; i++) {
    breaks.push({
      min: kclass[i],
      max: kclass[i + 1],
    });
  }

  // Guard against a degenerate matrix result (e.g. more classes than unique values).
  if (breaks.some((b) => !Number.isFinite(b.min) || !Number.isFinite(b.max))) {
    return calculateQuantile(sortedValues, Math.min(classes, n));
  }

  return breaks;
};

/**
 * Build the lower-class-limits and variance-combinations matrices used by Jenks.
 * Port of the standard Fisher–Jenks DP (same structure as simple-statistics / geostats).
 */
const jenksMatrices = (data: number[], nClasses: number) => {
  const n = data.length;

  // lowerClassLimits[i][j] = index of the left bound of class j when classifying
  // the first i values into j classes (1-based data indices).
  const lowerClassLimits: number[][] = Array.from({ length: n + 1 }, () => Array(nClasses + 1).fill(0));
  const varianceCombinations: number[][] = Array.from({ length: n + 1 }, () => Array(nClasses + 1).fill(0));

  for (let i = 1; i <= nClasses; i++) {
    lowerClassLimits[1][i] = 1;
    varianceCombinations[1][i] = 0;
    for (let j = 2; j <= n; j++) {
      varianceCombinations[j][i] = Infinity;
    }
  }

  for (let l = 2; l <= n; l++) {
    let sum = 0;
    let sumSquares = 0;
    let w = 0;

    // Build the variance of data[m-1 .. l-1] as m walks left from l.
    for (let m = 1; m <= l; m++) {
      const lowerClassLimit = l - m + 1;
      const val = data[lowerClassLimit - 1];

      w += 1;
      sum += val;
      sumSquares += val * val;

      // Variance of the current potential class [lowerClassLimit .. l].
      const variance = sumSquares - (sum * sum) / w;
      const i4 = lowerClassLimit - 1;

      if (i4 !== 0) {
        for (let j = 2; j <= nClasses; j++) {
          // If previous optimal + this class variance is better, take the break.
          const candidate = varianceCombinations[i4][j - 1] + variance;
          if (candidate <= varianceCombinations[l][j]) {
            lowerClassLimits[l][j] = lowerClassLimit;
            varianceCombinations[l][j] = candidate;
          }
        }
      }
    }

    lowerClassLimits[l][1] = 1;
    varianceCombinations[l][1] = sumSquares - (sum * sum) / w;
  }

  return { lowerClassLimits, varianceCombinations };
};

/** Backtrack lowerClassLimits to produce ordered break values including min and max. */
const jenksBreaks = (data: number[], lowerClassLimits: number[][], nClasses: number): number[] => {
  const n = data.length;
  const kclass: number[] = Array(nClasses + 1).fill(0);
  kclass[nClasses] = data[n - 1];
  kclass[0] = data[0];

  let countNum = nClasses;
  let k = n;

  while (countNum > 1) {
    const idx = lowerClassLimits[k][countNum] - 2;
    kclass[countNum - 1] = data[Math.max(0, idx)];
    k = lowerClassLimits[k][countNum] - 1;
    countNum -= 1;
  }

  return kclass;
};

const calculateHeadsTails = (sortedValues: number[], classes: number): ClassBreak[] => {
  const breaks: ClassBreak[] = [];
  let remainingValues = sortedValues;

  for (let i = 0; i < classes; i++) {
    if (remainingValues.length === 0) break;

    const mean = remainingValues.reduce((a, b) => a + b, 0) / remainingValues.length;
    const head = remainingValues.filter((v) => v <= mean);
    const tail = remainingValues.filter((v) => v > mean);

    // If everything is on one side of the mean, close out with the remainder.
    if (head.length === 0 || (tail.length === 0 && i < classes - 1)) {
      breaks.push({
        min: remainingValues[0],
        max: remainingValues[remainingValues.length - 1],
      });
      break;
    }

    breaks.push({
      min: head[0],
      max: head[head.length - 1],
    });

    remainingValues = tail;
  }

  // Last class should extend to the global max when we still have a tail.
  if (breaks.length && remainingValues.length) {
    const last = breaks[breaks.length - 1];
    // If the loop exited because classes ran out while a tail remains, fold the
    // tail into a final class when we still have room; otherwise extend last max.
    if (breaks.length < classes) {
      breaks.push({
        min: remainingValues[0],
        max: remainingValues[remainingValues.length - 1],
      });
    } else {
      last.max = remainingValues[remainingValues.length - 1];
    }
  }

  return breaks;
};

/**
 * Ensure breaks are ordered, abut, and span [globalMin, globalMax]. Used after
 * quantile when duplicate values collapse some class edges.
 */
const normalizeAbuttingBreaks = (breaks: ClassBreak[], globalMin: number, globalMax: number): ClassBreak[] => {
  if (!breaks.length) return [{ min: globalMin, max: globalMax }];

  const out = breaks.map((b) => ({ ...b }));
  out[0].min = globalMin;
  out[out.length - 1].max = globalMax;

  for (let i = 1; i < out.length; i++) {
    // Force abutting edges so SLD [min,max) filters don't leave gaps.
    out[i].min = out[i - 1].max;
    if (out[i].max < out[i].min) {
      out[i].max = out[i].min;
    }
  }

  return out;
};

export const pickColorsForBreaks = (colors: string[], breaksCount: number): string[] => {
  if (breaksCount <= 1) return [colors[0]];

  const step = (colors.length - 1) / (breaksCount - 1);

  const selected = [];
  for (let i = 0; i < breaksCount; i++) {
    const idx = Math.round(step * i);
    selected.push(colors[idx]);
  }
  return selected;
};
